import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, Table, Tag, Button, Space, Empty, Popconfirm, message, Drawer, Timeline, Descriptions, Input, Spin } from 'antd';
import {
  CheckOutlined, CloseOutlined, StopOutlined, EyeOutlined,
  ClockCircleOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined as StopCircle,
  WhatsAppOutlined, MessageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const STATUS_COLOR = {
  PENDING: 'blue', ACCEPTED: 'green', REJECTED: 'red', CANCELLED: 'default',
};
const STATUS_LABEL = {
  PENDING: 'Pendente', ACCEPTED: 'Aceito', REJECTED: 'Recusado', CANCELLED: 'Cancelado',
};

// Formata game_date que vem como string BR ("DD/MM/YYYY HH:mm") do backend.
function fmtGameDate(gd) {
  if (!gd) return null;
  if (typeof gd === 'string' && gd.includes('/')) return gd;
  const d = dayjs(gd);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : null;
}

// Sumario contextual da linha — "o que rolou" com um jogador.
// Substitui a coluna Status simples por uma explicacao curta.
function outcomeSummary(row, userType) {
  const gd = fmtGameDate(row.game_date);
  const other = userType === 'player' ? (row.team_name || 'time') : (row.player_name || 'jogador');
  const isSent = userType === 'player'
    ? !row.invited_by_team
    : row.invited_by_team;
  const respondedAgo = row.responded_at ? dayjs(row.responded_at).fromNow() : null;

  switch (row.status) {
    case 'PENDING':
      return isSent
        ? `Aguardando resposta do ${userType === 'player' ? 'time' : 'jogador'}`
        : `Aguarda sua resposta`;
    case 'ACCEPTED':
      if (row.kind === 'GENERIC') return `${userType === 'player' ? other : 'Você'} topou conversar${respondedAgo ? ` (${respondedAgo})` : ''}`;
      return userType === 'player'
        ? `Confirmado no jogo${gd ? ` de ${gd}` : ''}`
        : `${other} entrou no jogo${gd ? ` de ${gd}` : ''}`;
    case 'REJECTED':
      return userType === 'player'
        ? `${other} recusou${respondedAgo ? ` ${respondedAgo}` : ''}`
        : `Você recusou${respondedAgo ? ` ${respondedAgo}` : ''}`;
    case 'CANCELLED':
      return isSent ? 'Você cancelou' : `${other} cancelou`;
    default:
      return '';
  }
}

// Chat inline dentro do drawer. Carrega mensagens ao mount, envia com POST,
// e faz refetch simples ao enviar. Sem realtime — usuario apertando F5 ou
// reabrindo drawer pega mensagens novas. Poll leve a cada 20s enquanto
// drawer aberto pra pegar respostas do outro lado.
function InviteChat({ inviteId, userType, authHeaders }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/community/invites/${inviteId}/messages`, { headers: authHeaders });
      setMsgs(r.data.messages || []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, [inviteId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const r = await api.post(`/community/invites/${inviteId}/messages`, { body }, { headers: authHeaders });
      setMsgs(prev => [...prev, r.data.message]);
      setText('');
    } catch (e) {
      message.error(e?.response?.data?.error || 'Falhou');
    } finally { setSending(false); }
  };

  const myType = userType;
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 12 }}><MessageOutlined /> Conversa</div>
      <div ref={listRef} style={{
        background: '#fafafa', borderRadius: 10, padding: 10, minHeight: 120, maxHeight: 260,
        overflowY: 'auto', marginBottom: 8,
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
        ) : msgs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 12 }}>
            Nenhuma mensagem ainda. Manda a primeira!
          </div>
        ) : (
          msgs.map(m => {
            const mine = m.sender_type === myType;
            return (
              <div key={m.id} style={{
                display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 6,
              }}>
                <div style={{
                  maxWidth: '78%',
                  background: mine ? '#25D366' : '#fff',
                  color: mine ? '#fff' : '#222',
                  border: mine ? 'none' : '1px solid #eee',
                  padding: '6px 10px', borderRadius: 12, fontSize: 13,
                }}>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                  <div style={{ fontSize: 10, opacity: 0.75, marginTop: 3, textAlign: 'right' }}>
                    {dayjs(m.created_at).format('DD/MM HH:mm')}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Input.TextArea
          value={text} onChange={e => setText(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Manda uma mensagem..."
          onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={sending}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={send} loading={sending}
          disabled={!text.trim()}>Enviar</Button>
      </div>
      <div style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
        Enter pra enviar · Shift+Enter pra nova linha
      </div>
    </div>
  );
}

export default function ConvitesPanel({ userType }) {
  const [received, setReceived] = useState([]);
  const [sent, setSent]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [drawer, setDrawer]     = useState({ open: false, row: null });

  // Bug conhecido em api.js: o interceptor manda community_token pra qualquer URL
  // que contenha "/community/", mas sessão de organizador só tem "token" no
  // localStorage (community_token é exclusivo de jogador, setado em PlayerLogin.js).
  // Toda página de organizador que já chama /community/* (TeamDashboard.js,
  // TeamCommunity.js, TeamEdit.js, TeamInviteHistory.js, OrganizerMap.js) contorna
  // isso passando o header explícito — repetindo o mesmo padrão aqui.
  const authHeaders = useMemo(() => (
    userType === 'organizer'
      ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
      : undefined
  ), [userType]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (userType === 'player') {
        const [rx, tx] = await Promise.all([
          api.get('/community/invites/player'),
          api.get('/community/invites/player/applications?status=ALL'),
        ]);
        setReceived(rx.data.invites || rx.data || []);
        setSent(tx.data.applications || tx.data || []);
      } else {
        // organizer: GET /community/invites/team retorna array flat com invited_by_team
        // true = convite time->jogador (sent), false = candidatura jogador->time (received).
        const r = await api.get('/community/invites/team', { headers: authHeaders });
        if (Array.isArray(r.data)) {
          setReceived(r.data.filter((x) => !x.invited_by_team));
          setSent(r.data.filter((x) => x.invited_by_team));
        } else {
          setReceived(r.data.received || []);
          setSent(r.data.sent || []);
        }
      }
    } catch (e) {
      message.error('Erro ao carregar convites');
    } finally { setLoading(false); }
  }, [userType, authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Escuta 2 gatilhos pra abrir drawer via link externo:
  // 1. URL query param ?invite_id=X ao chegar na pagina (via redirect)
  // 2. Evento custom 'convites:open-drawer' ({id}) do NotificationCenter
  //    quando ja esta na mesma pagina (sem reload).
  useEffect(() => {
    const openById = (id) => {
      const found = [...received, ...sent].find(x => String(x.id) === String(id));
      if (found) setDrawer({ open: true, row: found });
    };
    // 1. URL
    const p = new URLSearchParams(window.location.search);
    const qId = p.get('invite_id');
    if (qId && (received.length || sent.length)) {
      openById(qId);
      p.delete('invite_id');
      const q = p.toString();
      window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''));
    }
    // 2. Evento
    const handler = (e) => openById(e.detail?.id);
    window.addEventListener('convites:open-drawer', handler);
    return () => window.removeEventListener('convites:open-drawer', handler);
  }, [received, sent]);

  const respond = async (id, status) => {
    try {
      await api.put(`/community/invites/${id}/respond`, { status }, { headers: authHeaders });
      message.success(status === 'ACCEPTED' ? 'Aceito! Jogador entrou no jogo.' : 'Recusado.');
      load();
    } catch { message.error('Falhou'); }
  };

  const cancel = async (id) => {
    try {
      await api.delete(`/community/invites/${id}`, { headers: authHeaders });
      message.success('Cancelado');
      load();
    } catch { message.error('Falhou'); }
  };

  const cols = [
    { title: userType === 'player' ? 'Time' : 'Jogador',
      dataIndex: 'display_name',
      render: (_, r) => <b>{r.team_name || r.player_name || '-'}</b> },
    { title: 'Jogo', dataIndex: 'game_label',
      render: (_, r) => {
        if (r.kind === 'GENERIC') return <Tag>sem jogo (conhecer)</Tag>;
        const gd = fmtGameDate(r.game_date);
        return gd || '-';
      } },
    { title: 'Status', dataIndex: 'status', width: 100,
      render: v => <Tag color={STATUS_COLOR[v] || 'default'}>{STATUS_LABEL[v] || v}</Tag> },
    { title: 'O que rolou', key: 'outcome',
      render: (_, r) => <span style={{ fontSize: 12, color: '#555' }}>{outcomeSummary(r, userType)}</span> },
    { title: 'Quando', dataIndex: 'created_at', width: 110,
      render: v => v ? dayjs(v).format('DD/MM HH:mm') : '-' },
    { title: '', key: 'detail', width: 60,
      render: (_, r) => (
        <Button size="small" type="text" icon={<EyeOutlined />}
                onClick={() => setDrawer({ open: true, row: r })}>ver</Button>
      ),
    },
  ];

  const recCols = [...cols.slice(0, 5), {
    title: 'Ação',
    render: (_, r) => r.status !== 'PENDING' ? cols[5].render(_, r) : (
      <Space wrap>
        <Button size="small" icon={<MessageOutlined />}
                onClick={() => setDrawer({ open: true, row: r })}>Conversar</Button>
        <Button size="small" type="primary" icon={<CheckOutlined />}
                onClick={() => respond(r.id, 'ACCEPTED')}>Aceitar</Button>
        <Button size="small" danger icon={<CloseOutlined />}
                onClick={() => respond(r.id, 'REJECTED')}>Recusar</Button>
      </Space>
    ),
  }];

  const sentCols = [...cols.slice(0, 5), {
    title: 'Ação',
    render: (_, r) => r.status !== 'PENDING' ? cols[5].render(_, r) : (
      <Space>
        <Popconfirm title="Cancelar esse convite?" onConfirm={() => cancel(r.id)}>
          <Button size="small" icon={<StopOutlined />}>Cancelar</Button>
        </Popconfirm>
      </Space>
    ),
  }];

  const tabs = [
    { key: 'received', label: `Recebidos (${received.length})`,
      children: received.length
        ? <Table rowKey="id" size="small" columns={recCols} dataSource={received} loading={loading} pagination={{ pageSize: 10 }} />
        : <Empty description="Nenhum convite recebido" /> },
    { key: 'sent', label: `Enviados (${sent.length})`,
      children: sent.length
        ? <Table rowKey="id" size="small" columns={sentCols} dataSource={sent} loading={loading} pagination={{ pageSize: 10 }} />
        : <Empty description="Nenhum convite enviado" /> },
    { key: 'all', label: 'Todos',
      children: <Table rowKey="id" size="small" columns={cols}
                       dataSource={[...received, ...sent]} loading={loading} pagination={{ pageSize: 10 }} /> },
  ];

  const r = drawer.row;
  const drawerIsSent = r ? (userType === 'player' ? !r.invited_by_team : r.invited_by_team) : false;
  const gd = r ? fmtGameDate(r.game_date) : null;
  const other = r ? (userType === 'player' ? (r.team_name || 'Time') : (r.player_name || 'Jogador')) : '';

  return (
    <>
      <Tabs items={tabs} defaultActiveKey="received" />

      <Drawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={r ? `Convite · ${other}` : 'Convite'}
        width={480}
      >
        {r && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Tag color={STATUS_COLOR[r.status] || 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
                {STATUS_LABEL[r.status] || r.status}
              </Tag>
              <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>
                {outcomeSummary(r, userType)}
              </span>
            </div>

            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={userType === 'player' ? 'Time' : 'Jogador'}>{other}</Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {r.kind === 'GENERIC' ? 'Conhecer o time (sem jogo específico)' : 'Jogo específico'}
              </Descriptions.Item>
              {gd && <Descriptions.Item label="Jogo">{gd}</Descriptions.Item>}
              {r.message && <Descriptions.Item label="Mensagem">{r.message}</Descriptions.Item>}
              {r.city && <Descriptions.Item label="Local">{r.neighborhood ? `${r.neighborhood}, ` : ''}{r.city}</Descriptions.Item>}
              {r.guest_price && <Descriptions.Item label="Valor avulso">R$ {r.guest_price}</Descriptions.Item>}
            </Descriptions>

            {/* Canal de contato: aparece so quando ACCEPTED, com telefone
                do outro lado. Backend so devolve contact_phone se o convite
                estiver aceito — antes disso e privado. */}
            {r.status === 'ACCEPTED' && r.contact_phone && (() => {
              const digits = String(r.contact_phone).replace(/\D/g, '');
              // Se ja veio 55, mantem; senao prefixa 55.
              const intl = digits.startsWith('55') ? digits : `55${digits}`;
              const myName = userType === 'player'
                ? (JSON.parse(localStorage.getItem('community_user') || '{}').name || 'um jogador')
                : (JSON.parse(localStorage.getItem('user') || '{}').username || 'organizador');
              const contextGame = gd ? ` do jogo de ${gd}` : '';
              const preset = userType === 'player'
                ? `Olá! Sou ${myName}, meu convite pro ${r.team_name || 'time'}${contextGame} foi aceito. Podemos combinar os detalhes por aqui?`
                : `Olá ${r.player_name || 'jogador'}! Aceitei sua candidatura${contextGame} pro ${JSON.parse(localStorage.getItem('user') || '{}').game_name || 'time'}. Bora combinar os detalhes?`;
              const wa = `https://wa.me/${intl}?text=${encodeURIComponent(preset)}`;
              return (
                <div style={{
                  background: '#e8f5e9', border: '1px solid #b7eb8f', borderRadius: 12,
                  padding: 14, marginBottom: 16,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#128C7E', marginBottom: 4 }}>
                    <WhatsAppOutlined /> Bora conversar!
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                    {userType === 'player'
                      ? 'Combina os detalhes com o organizador direto no WhatsApp.'
                      : 'Fala com o jogador e alinha os detalhes.'}
                  </div>
                  <a href={wa} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                    <Button type="primary" icon={<WhatsAppOutlined />} block
                      style={{ background: '#25D366', borderColor: '#25D366', fontWeight: 700 }}>
                      Falar com {r.contact_name || (userType === 'player' ? 'organizador' : 'jogador')} no WhatsApp
                    </Button>
                  </a>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'center' }}>
                    {r.contact_phone}
                  </div>
                </div>
              );
            })()}

            <div style={{ fontWeight: 600, marginBottom: 12 }}>Linha do tempo</div>
            <Timeline
              items={[
                {
                  color: 'blue',
                  dot: <SendOutlined />,
                  children: (
                    <>
                      <div><b>{drawerIsSent ? 'Você enviou' : `${other} enviou`}</b></div>
                      <div style={{ fontSize: 12, color: '#888' }}>{dayjs(r.created_at).format('DD/MM/YYYY [às] HH:mm')} · {dayjs(r.created_at).fromNow()}</div>
                    </>
                  ),
                },
                r.status === 'ACCEPTED' && {
                  color: 'green',
                  dot: <CheckCircleOutlined />,
                  children: (
                    <>
                      <div><b>{drawerIsSent ? `${other} aceitou` : 'Você aceitou'}</b></div>
                      {r.responded_at && <div style={{ fontSize: 12, color: '#888' }}>{dayjs(r.responded_at).format('DD/MM/YYYY [às] HH:mm')} · {dayjs(r.responded_at).fromNow()}</div>}
                      {r.kind === 'GAME' && (
                        <div style={{ fontSize: 12, color: '#25D366', marginTop: 4 }}>
                          ✓ Jogador confirmado na lista do jogo
                        </div>
                      )}
                    </>
                  ),
                },
                r.status === 'REJECTED' && {
                  color: 'red',
                  dot: <CloseCircleOutlined />,
                  children: (
                    <>
                      <div><b>{drawerIsSent ? `${other} recusou` : 'Você recusou'}</b></div>
                      {r.responded_at && <div style={{ fontSize: 12, color: '#888' }}>{dayjs(r.responded_at).format('DD/MM/YYYY [às] HH:mm')} · {dayjs(r.responded_at).fromNow()}</div>}
                    </>
                  ),
                },
                r.status === 'CANCELLED' && {
                  color: 'gray',
                  dot: <StopCircle />,
                  children: (
                    <>
                      <div><b>Cancelado</b></div>
                      {r.updated_at && <div style={{ fontSize: 12, color: '#888' }}>{dayjs(r.updated_at).format('DD/MM/YYYY [às] HH:mm')}</div>}
                    </>
                  ),
                },
                r.status === 'PENDING' && {
                  color: 'gray',
                  dot: <ClockCircleOutlined />,
                  children: (
                    <>
                      <div><b>Aguardando resposta</b></div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {drawerIsSent
                          ? `O ${userType === 'player' ? 'organizador' : 'jogador'} ainda não respondeu.`
                          : `Você ainda não respondeu.`}
                      </div>
                    </>
                  ),
                },
              ].filter(Boolean)}
            />

            {r.status === 'PENDING' && (
              <div style={{ marginTop: 20 }}>
                {drawerIsSent ? (
                  <Popconfirm title="Cancelar esse convite?" onConfirm={() => { cancel(r.id); setDrawer({ open: false, row: null }); }}>
                    <Button icon={<StopOutlined />} block>Cancelar convite</Button>
                  </Popconfirm>
                ) : (
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button type="primary" icon={<CheckOutlined />}
                            onClick={() => { respond(r.id, 'ACCEPTED'); setDrawer({ open: false, row: null }); }}>Aceitar</Button>
                    <Button danger icon={<CloseOutlined />}
                            onClick={() => { respond(r.id, 'REJECTED'); setDrawer({ open: false, row: null }); }}>Recusar</Button>
                  </Space>
                )}
              </div>
            )}

            {/* Chat inline — sempre disponivel, independente de status.
                Ate em convite recusado da pra mandar msg de despedida boleira. */}
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <InviteChat inviteId={r.id} userType={userType} authHeaders={authHeaders} />
            </div>
          </>
        )}
      </Drawer>
    </>
  );
}
